import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, FormControl, ValidationErrors } from '@angular/forms';
import { AspNetEmailValidationRegex, AtLeast8CharsAndAtLeastOneNumberRegex } from 'src/app/shared/constants/email-validation';
import { RegisterUserRequest, RegisterUserResponse } from 'identity-webapi-client';
import { AuthService } from 'src/app/services';
import { ConfigService } from 'src/app/services/config.service';


@Component({
  selector: 'app-credentials',
  templateUrl: './credentials.component.html',
  styleUrls: ['./credentials.component.scss']
})
export class CredentialsComponent implements OnInit {
  get controls() { return this?.formGroupControls?.controls!; }

  formGroupControls: FormGroup | null = null;
  isRegister: boolean = true;
  returnAuthUrl?: string;
  externalAuthUrl: string = ""
  message: string = "";
  stateError: boolean = false;
  loading: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    if (this.router.url.startsWith('/login')) this.isRegister = false;
    const state = this.route.snapshot.queryParamMap.get("state");
    this.returnAuthUrl = this.route.snapshot.queryParamMap.get("ReturnUrl")?.replace(this.configService.config.userManagerSettings.authority, "");
    if (this.returnAuthUrl) {
      this.returnAuthUrl = `/${this.returnAuthUrl}`;
      var url = new URL(this.configService.config.userManagerSettings.authority);
      url.pathname = "account/external/login";
      url.searchParams.set("provider", "Microsoft");
      url.searchParams.set("returnUrl", this.returnAuthUrl);
      this.externalAuthUrl = url.toString();
    } else {
      this.authService.createSigninRequest().then(x => {
        this.returnAuthUrl = `/connect/authorize/callback?client_id=${x.state._client_id}`
        + `&redirect_uri=${x.state._redirect_uri}`
        + `&response_type=code`
        + `&scope=${x.state._scope}`
        + `&response_mode=${x.state._response_mode}`
        + `&state=${x.state._id}`
        + `&code_challenge=${x.state._code_challenge}`
        + `&code_challenge_method=S256`;
        var url = new URL(x.state._authority);
        url.pathname = "account/external/login";
        url.searchParams.set("provider", "Microsoft");
        url.searchParams.set("returnUrl", this.returnAuthUrl);
        this.externalAuthUrl = url.toString();
      });
    }

    if (state) {
      this.processState(state, true)
    };

    this.formGroupControls = this.formBuilder.group({
      email: new FormControl('', [Validators.required, Validators.pattern(AspNetEmailValidationRegex)]),
      password: new FormControl('', [Validators.required, Validators.pattern(AtLeast8CharsAndAtLeastOneNumberRegex)]),
      confirmPassword: new FormControl('', [() => {
        if (!!this.controls?.password && this.isRegister) {
          const pass = this.controls?.password?.value;
          const confirmPass = this.controls?.confirmPassword?.value;
          const valErrors: ValidationErrors = pass === confirmPass ? {} : { notSame: true };
          return valErrors;
        }
        return {};
      }]),
    });
  }

  onSubmit(): void {
    if (this.isRegister) {
      this.loading = true;
      var url = new URL(this.configService.config.userManagerSettings.authority);
      url.pathname = "account/email/sign-up";
      fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            email: this.controls.email.value,
            password: this.controls.password.value
        } as RegisterUserRequest)
      })
      .then(result => result.json())
      .then(json => {
        let response = json as RegisterUserResponse;
        if (response.success) {
          this.router.navigateByUrl(`/sign-up/verify-email?state=EmailSent&email=${this.controls.email.value}`);
        } else {
          const error = response?.errorMessages?.find((x: string) => typeof x !== undefined);
          if (error) {
            this.processState(error, true);
          }
        }
        this.loading = false;
      });
    } else {
      this.loading = true;
      var url = new URL(this.configService.config.userManagerSettings.authority);
      url.pathname = "account/email/login";
      fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            email: this.controls.email.value,
            password: this.controls.password.value,
            recaptcha: "",
            returnUrl: this.returnAuthUrl
        })
      })
      .then(result => result.json())
      .then(json => {
        let response = json;
        if (response.success) {
          this.router.navigateByUrl(`/sign-up/company`);
        } else {
          const error = response?.errorMessages?.find((x: string) => typeof x !== undefined);
          if (error) {
            this.processState(error, false);
          }
        }
        this.loading = false;
      });
    }
  }

  onMsClick() {
    this.loading = true;
    if (this.isRegister) {
      var url = new URL(this.configService.config.userManagerSettings.authority);
      url.pathname = "account/external/sign-up";
      url.searchParams.set("provider", "Microsoft");
      url.searchParams.set("returnUrl", encodeURIComponent(this.returnAuthUrl ?? ""));
      document.location.href = url.toString();
    } else {
      document.location.href = this.externalAuthUrl;
    }
  }

  toggleIsRegister() {
    this.isRegister = !this.isRegister;
    this.formGroupControls?.get('confirmPassword')?.updateValueAndValidity();
  }

  private processState(state: string, isRegister: boolean): void {
    switch (state) {
      case "AccountDisabled":
        this.isRegister = isRegister;
        this.stateError = true;
        this.message = "Your account is disabled.";
        break;
      case "AccountLocked":
        this.isRegister = isRegister;
        this.stateError = true;
        this.message = "Your account is locked.";
        break;
      case "DuplicateUserName":
      case "DuplicateEmail":
        this.isRegister = isRegister;
        this.stateError = true;
        this.message = "Account already exists.";
        break;
      case "EmailNotVerified":
        this.router.navigateByUrl(`/sign-up/verify-email?state=EmailSent&email=${this.controls.email.value}`);
        break;
      case "InvalidCredentials":
        this.isRegister = isRegister;
        this.stateError = true;
        this.message = "Your credentials are invalid.";
        break;
      case "LoginAccountNotFound":
        this.isRegister = false;
        this.stateError = true;
        this.message = "Your account could not be found.";
        break;
      case "LoginAccountDisabled":
        this.isRegister = false;
        this.stateError = true;
        this.message = "Your account is disabled.";
        break;
      case "LoginAccountLocked":
        this.isRegister = false;
        this.stateError = true;
        this.message = "Your account is locked.";
        break;
      case "LoginError":
        this.isRegister = false;
        this.stateError = true;
        this.message = "There was an error. Please try again.";
        break;
      default:
        this.isRegister = isRegister;
        this.stateError = true;
        this.message = "There was an error. Please try again.";
        break;
    }
  }
}
